import * as React from 'react';

import { FieldLabelRow } from '../Field';
import { Text } from '../Text';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValue } from './SelectPrimitives';

type SelectOption = {
  value: string;
  label: string;
  description?: string;
};

type SelectProps = {
  options: SelectOption[];
  value?: string;
  onValueChange?: (value: string | null) => void;
  /** Also fires when the popup closes without a pick, so forms can mark the field touched. */
  onBlur?: () => void;
  placeholder?: string;
  isClearable?: boolean;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  label?: string;
  isRequired?: boolean;
  tooltipText?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  labelTestId?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
};

function OptionLabel({ option }: { option: SelectOption }) {
  return (
    <span className="flex min-w-0 items-baseline gap-1 truncate">
      <Text className="max-w-full shrink-0 text-xs truncate">{option.label}</Text>
      {option.description && <Text className="min-w-0 text-xs text-grey-50 truncate">{option.description}</Text>}
    </span>
  );
}

/**
 * Single-value picker for a short, fixed list the user recognizes by name: statuses, yes/no, units, plan types,
 * roughly 10 options or fewer. Keyboard lookup is typeahead only. For a long or data-driven list (users, agencies,
 * counties, templates), options that need a description line, or anything the user searches rather than scans,
 * use `ComboBox`. A field that appears in several places uses the same control in all of them.
 */
function Select({
  options,
  value,
  onValueChange,
  onBlur,
  placeholder = 'Select...',
  isClearable = false,
  disabled,
  className,
  triggerClassName,
  id: idProp,
  ref,
  label,
  isRequired,
  tooltipText,
  secondaryAction,
  labelTestId,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'data-testid': testId,
}: SelectProps) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  // Radix reserves the empty string for "no selection" and throws on an item that uses it.
  const selectableOptions = options.filter((option) => option.value !== '');
  // Always controlled: a value with no matching option, or none yet, shows the placeholder rather than an empty trigger.
  const displayValue = value !== undefined && selectableOptions.some((option) => option.value === value) ? value : '';

  const control = (
    <SelectRoot
      value={displayValue}
      onValueChange={(next) => onValueChange?.(next === '' ? null : next)}
      onOpenChange={(open) => {
        if (!open) onBlur?.();
      }}
      isClearable={isClearable}
      disabled={disabled}
    >
      <SelectTrigger
        ref={ref}
        id={id}
        aria-label={label ? undefined : ariaLabel}
        aria-required={isRequired || undefined}
        clearLabel={label || ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        data-testid={testId}
        onBlur={(event) => {
          if (event.currentTarget.dataset.state !== 'open') onBlur?.();
        }}
        className={triggerClassName}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={className}>
        {selectableOptions.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            textValue={option.label}
            data-testid={testId ? `${testId}-option-${option.value}` : undefined}
          >
            <OptionLabel option={option} />
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );

  if (!label) return control;

  return (
    // Not `Field`: a label and one control need no group role, and it would nest inside FormItem's group.
    <div className="flex w-full flex-col gap-1 [&>*]:w-full">
      <FieldLabelRow
        htmlFor={id}
        isRequired={isRequired}
        tooltipText={tooltipText}
        secondaryAction={secondaryAction}
        tooltipTestId={testId ? `${testId}-tooltip` : undefined}
        data-testid={labelTestId}
      >
        {label}
      </FieldLabelRow>
      {control}
    </div>
  );
}

export { Select };
export type { SelectOption, SelectProps };
